import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaEdit, FaStickyNote, FaPaperPlane, FaSave, FaSortAmountDown, FaSortAmountUp, FaSort } from "react-icons/fa";
import Loader from "../../../component/common/loader";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { InquiryService } from "../../../service/admin/inquiryService";
import { formatDate } from "../../../helper/utils";
import NoImage from "../../../assets/images/common/importerr-no-image-logo.png";
import "./style.css";

const statusOptions = [
  { label: "Open", value: "open" },
  { label: "Responded", value: "responded" },
  { label: "Closed", value: "closed" },
];

const statusClassName = (currentStatus) => {
  if (!currentStatus) return "neutral";
  if (currentStatus === "open") return "warning";
  if (currentStatus === "responded") return "info";
  if (currentStatus === "closed") return "success";
  return "neutral";
};

const InfoItem = ({ label, value, fallback = "-" }) => (
  <div className="dt-info-item">
    <span className="dt-info-label">{label}</span>
    <span className="dt-info-value">{value || fallback}</span>
  </div>
);

const AdminInquiryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const message = useMessage();

  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [replySending, setReplySending] = useState(false);
  const [statusValue, setStatusValue] = useState("open");
  const [replyText, setReplyText] = useState("");
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [noteAdminName, setNoteAdminName] = useState("");
  const [noteText, setNoteText] = useState("");
  const notesListRef = useRef(null);
  const [qtySort, setQtySort] = useState("none");

  useEffect(() => {
    if (id) {
      fetchInquiry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInquiry = async () => {
    setLoading(true);
    try {
      const res = await InquiryService.getInquiryById(id);
      if (res?.status?.code === 200) {
        setInquiry(res?.data);
        setStatusValue(res?.data?.status || "open");
      } else if (res?.status?.code === 404) {
        message.showToast("Inquiry not found", ToastTypes.ERROR);
        navigate("/admin/inquiries");
      } else if (res?.status?.code === 402) {
        navigate("/login");
      } else {
        message.showToast(
          res?.status?.message || "Failed to load inquiry",
          ToastTypes.ERROR
        );
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to load inquiry", ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    setSavingStatus(true);
    try {
      const res = await InquiryService.updateInquiry(id, { status: statusValue });
      if (res?.status?.code === 200) {
        setInquiry(res?.data);
        message.showToast("Inquiry status updated", ToastTypes.SUCCESS);
      } else if (res?.status?.code === 402) {
        navigate("/login");
      } else {
        message.showToast(res?.status?.message || "Update failed", ToastTypes.ERROR);
      }
    } catch (error) {
      message.showToast(error?.message || "Update failed", ToastTypes.ERROR);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) {
      message.showToast("Please type a reply message", ToastTypes.WARNING);
      return;
    }
    setReplySending(true);
    try {
      const res = await InquiryService.replyToInquiry(id, { message: replyText.trim() });
      if (res?.status?.code === 200) {
        setInquiry(res?.data);
        setReplyText("");
        message.showToast("Reply sent to user", ToastTypes.SUCCESS);
      } else if (res?.status?.code === 402) {
        navigate("/login");
      } else {
        message.showToast(res?.status?.message || "Failed to send reply", ToastTypes.ERROR);
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to send reply", ToastTypes.ERROR);
    } finally {
      setReplySending(false);
    }
  };

  const senderLabel = (entry) =>
    entry.sender === "admin"
      ? "You"
      : inquiry?.user?.fullName || inquiry?.user?.firstName || "User";

  const snapshot = inquiry?.productSnapshot || {};
  const productTitle =
    snapshot.title || inquiry?.product?.title || inquiry?.productSku || "-";
  const productSku = inquiry?.productSku || snapshot.sku || inquiry?.product?.sku;
  const productImage = snapshot.imageUrl || inquiry?.product?.imageUrl || "";
  const productPrice =
    snapshot.price ||
    inquiry?.product?.price ||
    inquiry?.product?.sp ||
    inquiry?.product?.sp ||
    null;
  const totalQuantity = inquiry?.totalQuantity;
  const variants = inquiry?.variantLines || [];

  // Handle product click open PDP
  const handleProduct = (product) => {
    const url = `/product-details?ali=1&offer=${product.sku}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Handle Edit Price click — open product edit or draft edit page
  const handleEditPrice = () => {
    const product = inquiry?.product;
    if (!product?.id) return;
    const status = String(product.status);
    if (status === "6") {
      window.open(`/admin/product-drafts/edit/${product.id}`, "_blank", "noopener,noreferrer");
    } else {
      window.open(`/admin/products/edit/${product.id}`, "_blank", "noopener,noreferrer");
    }
  };
  // function to sort by quantity
  const getSortedVariants = () => {
    if (!variants?.length) return [];

    if (qtySort === "asc") {
      return [...variants].sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
    }
    if (qtySort === "desc") {
      return [...variants].sort((a, b) => (b.quantity || 0) - (a.quantity || 0));
    }

    return variants; // none
  };

  const handleSaveNote = async () => {
    if (!noteAdminName.trim()) {
      message.showToast("Please enter admin name", ToastTypes.WARNING);
      return;
    }
    if (!noteText.trim()) {
      message.showToast("Please write a note", ToastTypes.WARNING);
      return;
    }

    setNotesSaving(true);
    try {
      const res = await InquiryService.addNote(id, {
        adminName: noteAdminName.trim(),
        note: noteText.trim(),
      });

      if (res?.status?.code === 200) {
        setInquiry(res?.data);
        setNoteText("");
        message.showToast("Note added", ToastTypes.SUCCESS);
      } else if (res?.status?.code === 402) {
        navigate("/login");
      } else {
        message.showToast(
          res?.status?.message || "Failed to add note",
          ToastTypes.ERROR
        );
      }
    } catch (error) {
      message.showToast(
        error?.message || "Failed to add note",
        ToastTypes.ERROR
      );
    } finally {
      setNotesSaving(false);
    }
  };

  const adminNotes = inquiry?.adminNotes || [];
  const sortedNotes = [...adminNotes].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt) // oldest top, latest bottom
  );
  const hasNotes = sortedNotes.length > 0;

  useEffect(() => {
    if (notesModalOpen && notesListRef.current) {
      const el = notesListRef.current;
      // scroll to bottom so latest (last) note always visible
      el.scrollTop = el.scrollHeight;
    }
  }, [notesModalOpen, sortedNotes.length]);

  return (
    <div className="dt-page">
      {/* ── Top Bar ── */}
      <div className="dt-topbar">
        <button className="dt-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <form className="dt-topbar-center" onSubmit={handleStatusUpdate}>
          <span className="dt-topbar-label">Status</span>
          <select value={statusValue} onChange={(e) => setStatusValue(e.target.value)}>
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button type="submit" className="dt-btn dt-btn-primary" disabled={savingStatus}>
            <FaSave /> {savingStatus ? "Saving..." : "Update"}
          </button>
        </form>
        <div className="dt-topbar-right">
          <button
            type="button"
            className="dt-action-btn"
            onClick={inquiry?.product?.id ? handleEditPrice : undefined}
            disabled={!inquiry?.product?.id}
            title={!inquiry?.product?.id ? "Product not saved yet" : "Edit Price"}
          >
            <FaEdit /> Edit Price
          </button>
          <button
            type="button"
            className={`dt-action-btn ${hasNotes ? "dt-action-highlight" : ""}`}
            onClick={() => setNotesModalOpen(true)}
          >
            <FaStickyNote /> {hasNotes ? `Notes (${sortedNotes.length})` : "Add Note"}
          </button>
        </div>
      </div>

      {/* ── Info Grid ── */}
      <div className="dt-grid">
        {/* User Card */}
        <div className="dt-card">
          <div className="dt-card-header">
            <h3>User Details</h3>
            <span className="dt-card-badge">{inquiry?.user?.email || ""}</span>
          </div>
          <div className="dt-info-grid">
            <InfoItem label="Name" value={inquiry?.user?.fullName || inquiry?.user?.firstName} />
            <InfoItem label="Phone" value={inquiry?.user?.phone} />
            <InfoItem label="Orders" value={typeof inquiry?.user?.totalOrders === "number" ? inquiry.user.totalOrders : undefined} />
            <InfoItem
              label="Paid Amount"
              value={typeof inquiry?.user?.totalPaidAmount === "number" ? `₹${Number(inquiry.user.totalPaidAmount).toLocaleString()}` : undefined}
            />
            <InfoItem
              label="Subscribed"
              value={typeof inquiry?.user?.totalSubscribed === "number" ? inquiry.user.totalSubscribed : undefined}
            />
          </div>
        </div>

        {/* Product Card */}
        <div className="dt-card">
          <div className="dt-card-header">
            <h3>Product Details</h3>
          </div>
          <div className="dt-product-row">
            <div className="dt-product-img" onClick={() => handleProduct(inquiry?.product || snapshot)}>
              <img
                src={productImage || NoImage}
                alt={productTitle}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = NoImage; }}
                referrerPolicy="no-referrer"
              />
              <span className="dt-product-view">View</span>
            </div>
            <div className="dt-info-grid" style={{ flex: 1 }}>
              <InfoItem label="Title" value={productTitle} />
              <InfoItem label="SKU" value={productSku} />
              <InfoItem label="Total Qty" value={typeof totalQuantity === "number" && totalQuantity > 0 ? totalQuantity : undefined} />
              <InfoItem label="Variants" value={variants?.length ? variants.length : undefined} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Variants ── */}
      {variants && variants.length > 0 && (
        <div className="dt-card">
          <div className="dt-card-header">
            <h3>Variant Requirements</h3>
          </div>
          <div className="dt-table-wrap">
            <table className="dt-table">
              <thead>
                <tr>
                  <th>Variant</th>
                  <th>Attributes</th>
                  <th>Unit Price</th>
                  <th
                    className="dt-sortable-th"
                    onClick={() => setQtySort(qtySort === "none" ? "asc" : qtySort === "asc" ? "desc" : "none")}
                  >
                    Quantity
                    {qtySort === "asc" && <FaSortAmountUp className="dt-sort-icon" />}
                    {qtySort === "desc" && <FaSortAmountDown className="dt-sort-icon" />}
                    {qtySort === "none" && <FaSort className="dt-sort-icon dt-sort-neutral" />}
                  </th>
                </tr>
              </thead>
              <tbody>
                {getSortedVariants().map((v, idx) => (
                  <tr key={v.skuId || idx}>
                    <td>
                      <div className="dt-variant-cell">
                        {v.imageUrl && (
                          <img
                            className="dt-variant-thumb"
                            src={v.imageUrl}
                            alt={v.label || v.skuId}
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = NoImage; }}
                            referrerPolicy="no-referrer"
                          />
                        )}
                        <div>
                          <div className="dt-variant-label">{v.label || v.skuId || "Variant"}</div>
                          {v.skuId && <div className="dt-variant-sku">SKU: {v.skuId}</div>}
                        </div>
                      </div>
                    </td>
                    <td>
                      {Array.isArray(v.attributes) && v.attributes.length ? (
                        <div className="dt-attrs">
                          {v.attributes.map((a, i) => (
                            <span key={i} className="dt-attr-tag">
                              {a.name || a.attributeName}: {a.value || a.attributeValue}
                            </span>
                          ))}
                        </div>
                      ) : "-"}
                    </td>
                    <td>{typeof v.unitPrice === "number" ? `₹${Number(v.unitPrice).toLocaleString()}` : "-"}</td>
                    <td className={v.quantity > 0 ? "dt-qty-highlight" : ""}>{v.quantity ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Conversation ── */}
      <div className="dt-card">
        <div className="dt-card-header">
          <h3>Conversation</h3>
          <div className="dt-meta-pills">
            <span>Created {formatDate(inquiry?.createdAt)}</span>
            <span>Updated {formatDate(inquiry?.updatedAt)}</span>
          </div>
        </div>
        <div className="dt-chat-feed">
          {inquiry?.messages?.length ? (
            inquiry.messages.map((entry, idx) => (
              <div key={`${entry.sentAt}-${idx}`} className={`dt-chat-row ${entry.sender}`}>
                <div className={`dt-chat-bubble ${entry.sender}`}>
                  <div className="dt-chat-meta">
                    <strong>{senderLabel(entry)}</strong>
                    <span>{formatDate(entry.sentAt)}</span>
                  </div>
                  <p>{entry.body}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="dt-empty">No messages yet.</p>
          )}
        </div>
      </div>

      {/* ── Reply ── */}
      <form className="dt-reply-form" onSubmit={handleReply}>
        <label>Reply to user</label>
        <div className="dt-reply-input-row">
          <textarea
            rows={2}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
          />
          <button type="submit" className="dt-btn dt-btn-send" disabled={replySending}>
            <FaPaperPlane /> {replySending ? "Sending..." : "Send"}
          </button>
        </div>
      </form>

      {/* ── Notes Modal ── */}
      {notesModalOpen && (
        <div className="notes-modal-backdrop" onClick={() => !notesSaving && setNotesModalOpen(false)}>
          <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notes-modal-header">
              <div>
                <h3>Admin Notes</h3>
                <p className="notes-modal-subtitle">Internal notes only visible in admin panel.</p>
              </div>
              <button type="button" className="icon-btn" onClick={() => setNotesModalOpen(false)} disabled={notesSaving}>✕</button>
            </div>
            <div className="notes-list" ref={notesListRef}>
              {sortedNotes.length ? (
                sortedNotes.map((note, index) => (
                  <div key={`${note.createdAt}-${index}`} className={`note-item ${index === sortedNotes.length - 1 ? "note-latest" : "note-old"}`}>
                    <div className="note-meta">
                      <span className="note-admin">{note.adminName || "Admin"}</span>
                      <span className="note-date">{formatDate(note.createdAt)}</span>
                    </div>
                    <p className="note-body">{note.note}</p>
                  </div>
                ))
              ) : (
                <p className="dt-empty">No notes yet. Add a note to help other admins.</p>
              )}
            </div>
            <div className="notes-form">
              <div className="form-field">
                <label>Admin Name</label>
                <input type="text" placeholder="Your Name" value={noteAdminName} onChange={(e) => setNoteAdminName(e.target.value)} />
              </div>
              <div className="form-field">
                <label>Note</label>
                <textarea rows={3} placeholder="Write a quick summary or next steps..." value={noteText} onChange={(e) => setNoteText(e.target.value)} />
              </div>
            </div>
            <div className="notes-modal-actions">
              <button type="button" className="ghost-btn" onClick={() => setNotesModalOpen(false)} disabled={notesSaving}>Close</button>
              <button type="button" className="primary-btn" onClick={handleSaveNote} disabled={notesSaving}>{notesSaving ? "Saving..." : "Save note"}</button>
            </div>
          </div>
        </div>
      )}

      <Loader
        value={loading || savingStatus || replySending}
        variant="overlay"
        text={replySending ? "Sending reply..." : savingStatus ? "Updating..." : "Loading..."}
      />
    </div>
  );
};

export default AdminInquiryDetails;
